/**
 * Sale process management
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';

/**
 * Material UI
 */
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { ProgressBar, Step } from 'react-step-progress-bar';

/**
 * Redux
 */
import { useSelector } from 'react-redux';
import { wizardState } from '../redux/wizardSlice';
import { leadState } from '../redux/leadSlice';
import { lookupsState } from '../redux/lookupsSlice';

/**
 * Components
 */
import Navigation from './wizard/Navigation';
import PersonalData from './wizard/PersonalData';
import TradeInWizard from './wizard/TradeInWizard';
import ServicesSelect from './wizard/ServicesSelect';
import PaymentSelect from './wizard/PaymentSelect';
import DocumentsUpload from './wizard/DocumentsUpload';
import ResumeContent from './wizard/ResumeContent';

import {
  Personal, TradeIn, Services, Payment, Documents, Summary, PayPal,
} from '../components/SVGIcons';
import PaypalStep from './wizard/PaypalStep';
import { steps } from '../helpers/const';

const useStyles = makeStyles((theme) => ({
  panel: {
    margin: '35px 0',
    textAlign: 'center',
  },
  panelTitle: {
    fontSize: '1.5em',
    lineHeight: '1.2em',
    fontWeight: 'bold',
    marginBottom: 5,
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8em',
    },
  },
}));

/**
 * @returns {JSX.Element}
 */
function WizardEcommerce({ showProgressBar }) {
  const classes = useStyles();
  const [loadPage, setLoadPage] = useState(false);
  const { t } = useTranslation();
  const { configuration } = useSelector(lookupsState);
  const { personalData, quoteSteps: subStepsState } = useSelector(leadState);
  const { quote: quoteLookup } = useSelector(lookupsState);
  const { tradeIn: hasTradeIn } = personalData;
  const { validStep, step, subStep } = useSelector(wizardState);
  const hasServices = (
    configuration?.services?.all_vehicles.filter((el) => el.type !== 'mandatory')?.length > 0
    || configuration?.services?.this_vehicle.filter((el) => el.type !== 'mandatory')?.length > 0
  ) ?? false;

  const isFinalTradeInStep = step === 1 && subStep === 3;

  const stepList = [
    {
      icon: <Personal />,
      percentage: 0,
      title: 'Buyer data',
      subTitle: 'Insert your data',
      submitData: true,
    },
    {
      icon: <TradeIn />,
      percentage: 30,
      title: 'Trade-in Vehicle Data',
      subTitle: 'Enter your vehicle data',
      finalTitle: 'Trade',
      finalSubTitle: 'Here is how much we value your used',
      submitData: true,
    },
    {
      icon: <Services />,
      percentage: 42,
      title: 'Services',
      subTitle: 'Select Additional services',
      submitData: true,
    },
    {
      icon: <Payment />,
      percentage: 59,
      title: 'Payment',
      subTitle: 'Select the type of payment',
      submitData: true,
    },
    {
      icon: <Documents />,
      percentage: 74,
      title: 'Documents',
      subTitle: 'Upload your identification documents',
      submitData: false,
    },
    {
      icon: <Summary />,
      percentage: 88,
      title: 'Data Summary',
      subTitle: 'Check the data entered',
      submitData: false,
    },
    {
      icon: <PayPal />,
      percentage: 100,
      title: 'PayPal',
      subTitle: 'Book your vehicle with PayPal',
      submitData: false,
    },
  ];

  const getPersonalDataNextStep = () => {
    if (hasTradeIn) {
      return steps.trade;
    }
    if (hasServices) {
      return steps.services;
    }
    return steps.payments;
  };

  const getPaymentsDocumentsPrevStep = () => {
    if (step === steps.documents && configuration.vehicle.price > 0) {
      return steps.payments;
    }
    if (hasServices) {
      return steps.services;
    }
    if (hasTradeIn) {
      return steps.trade;
    }
    return steps.personal_data;
  };

  useEffect(() => {
    // Prevent scroll at first page load.
    // For example if ecommerce is in vehicle detail
    if (loadPage) {
      const element = document.getElementById('ga-ecommerce-data');
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    } else {
      setLoadPage(true);
    }
  }, [step, subStep]);

  return (
    <>
      <div id="ga-ecommerce-progressbar">
        { showProgressBar && (
          <ProgressBar percent={stepList[step].percentage}>
            {stepList.map((el) => (
              <Step
                key={uuid()}
              >
                {({ accomplished }) => (
                  <div className={`indexedStep ${accomplished ? 'accomplished' : null}`}>
                    {el.icon}
                  </div>
                )}
              </Step>
            ))}
          </ProgressBar>
        ) }
      </div>
      <div id="ga-ecommerce-data" className={classes.panel}>
        <p className={classes.panelTitle}>
          {t(isFinalTradeInStep ? stepList[1].finalTitle : stepList[step].title)}
        </p>
        <p>
          {t(isFinalTradeInStep ? stepList[1].finalSubTitle : stepList[step].subTitle)}
        </p>
        {step === steps.personal_data && (
          <PersonalData
            showFinancialFlag={configuration?.financial?.financial === 'true'}
            showTradeFlag={configuration?.financial?.trade !== 'not-active'}
          />
        )}
        {step === steps.trade && hasTradeIn && <TradeInWizard />}
        {step === steps.services && <ServicesSelect />}
        {step === steps.payments && <PaymentSelect />}
        {step === steps.documents && <DocumentsUpload template="complete" />}
        {step === steps.resume && <ResumeContent />}
        {step === steps.paypal && <PaypalStep />}
      </div>
      {step === steps.personal_data
        && (
          <Navigation
            stepNextSize={getPersonalDataNextStep()}
            hidePrev
            enablePrev={false}
            enableNext={validStep === true}
            needSubmit
          />
        )}
      {step === steps.trade
        && (
          <Navigation
            enableNext={subStepsState[subStep].isValid}
            disabledNext={quoteLookup.versionsLoad}
            needSubmit={[2, 3].includes(subStep) && stepList[step].submitData}
            stepPrevSize={steps.personal_data}
            stepNextSize={hasServices ? steps.services : steps.payments}
          />
        )}
      {step === steps.services
        && (
          <Navigation
            stepPrevSize={hasTradeIn === true ? steps.trade : steps.personal_data}
            stepNextSize={steps.payments}
            enableNext={validStep === true}
            needSubmit={stepList[step].submitData}
          />
        )}
      {(step === steps.payments || step === steps.documents)
        && (
          <Navigation
            stepPrevSize={getPaymentsDocumentsPrevStep()}
            needSubmit={stepList[step].submitData}
            enableNext={validStep === true}
            stepNextSize={step + 1}
          />
        )}
      {step === steps.resume
        && (
          <Navigation
            stepPrevSize={steps.documents}
            enableNext
            needSubmit={stepList[step].submitData}
            stepNextSize={step + 1}
          />
        )}
      {step === steps.paypal
        && (
          <Navigation
            stepPrevSize={steps.resume}
            enableNext={false}
            needSubmit={stepList[step].submitData}
          />
        )}
    </>
  );
}
WizardEcommerce.propTypes = {
  showProgressBar: PropTypes.bool,
};

WizardEcommerce.defaultProps = {
  showProgressBar: true,
};
export default WizardEcommerce;
